import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { ListingResponse } from "../types/Listing";
import {
  CopyReportPayload,
  CreateCustomReportPayload,
  CreateCustomReportResponse,
  GetSharedReportInternallyPayload,
  ListingStandardReportFieldPayload,
  ModifyCustomReportPayload,
  ReportCategoryField,
  ReportCategoryFieldsPayload,
  ReportColumnFilterDropdownValuesPayload,
  ReportColumnFilterDropdownValuesResponse,
  ReportsByCategoryPayload,
  RunCustomReportPayload,
  RunReportDownloadProcessReponse,
  SharedReportInternallyPayload,
  SharedReportInternallyResponse,
  SharedReportViaEmailPayload,
  SharedReportViaEmailResponse,
  ShareReportListItem,
  ShareReportListPayload,
  SpecificReportPayload,
  StandardReportFieldResponse,
  StandardReportPayload,
  TReport,
  TReportWithFields,
} from "../types/Report";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS, RESPONSE_CODE } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

// Fetch request to download report
export const downloadReport = (
  s3Url: string,
  name: string,
  onSuccess?: () => void,
  onError?: () => void
) => {
  // const format = params.response_format;
  // const additionalHeader: HeadersInit = {}
  // additionalHeader["Content-Type"] = format === 'pdf' ? 'application/pdf' : format === 'xlsx' ? 'application/vnd.ms-excel' : 'text/csv'

  fetch(s3Url, {
    method: API_METHODS.GET,
    // headers: {
    //   'Authorization': preparedAuthHeader(localStorage.getItem(localStorageKeys.accessToken)),
    //   ...additionalHeader
    // },
  })
    .then((resp) => {
      return resp.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // the filename you want
      a.download = name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      onSuccess && onSuccess();
    })
    .catch(() => {
      onError && onError();
    });
};

export enum ReportApiTags {
  CUSTOM_REPORT_CREATED = "CUSTOM_REPORT_CREATED",

  SHARED_REPORT_INTERNALLY_CREATED = "sharedReportInternallyCreated",
  SHARED_REPORT_VIA_EMAIL_CREATED = "sharedReportViaEmailCreated",

  CUSTOM_REPORT_MODIFIED = "CUSTOM_REPORT_MODIFIED",
  SHARED_LIST = "SHARED_LIST",

  REPORT_COLUMN_FILTER_DROPDOWN_VALUES = "REPORT_COLUMN_FILTER_DROPDOWN_VALUES",
}

// Retry ONLY the per-user concurrency-cap 429 (code "too_many_concurrent_queries").
// A dashboard fans out many run-report calls at once; when the per-user cap is hit,
// a slot frees as sibling queries finish, so a short jittered backoff retry succeeds
// and the widget never surfaces an error. The dedup 429 (duplicate_query_in_progress)
// is deliberately NOT retried — it keeps its inline "already running" message; and
// 401 handling stays inside baseQueryWithAuthHandling.
const MAX_CONCURRENCY_RETRIES = 4;

const reportBaseQuery = retry(
  (args, api, extraOptions) =>
    baseQueryWithAuthHandling(appBaseQuery.withAuthJson, args, api, extraOptions),
  {
    // maxRetries is ignored when retryCondition is supplied, so bound via `attempt`.
    retryCondition: (error, _args, { attempt }) => {
      const status = (error as any)?.status;
      const code = (error as any)?.data?.code;
      return (
        status === RESPONSE_CODE.TOO_MANY_REQUESTS &&
        code === "too_many_concurrent_queries" &&
        attempt <= MAX_CONCURRENCY_RETRIES
      );
    },
    backoff: async (attempt) => {
      // 200/400/800/1600ms capped at 2s, plus jitter so all capped widgets do
      // not retry in lockstep (thundering herd).
      const ms = Math.min(200 * 2 ** (attempt - 1), 2000);
      await new Promise((resolve) =>
        setTimeout(resolve, ms + Math.random() * 150)
      );
    },
  }
);

export const ReportAPIs = createApi({
  reducerPath: "reportApi",
  tagTypes: [
    ReportApiTags.CUSTOM_REPORT_CREATED,

    ReportApiTags.SHARED_REPORT_INTERNALLY_CREATED,
    ReportApiTags.SHARED_REPORT_VIA_EMAIL_CREATED,

    ReportApiTags.CUSTOM_REPORT_MODIFIED,
    ReportApiTags.SHARED_LIST,

    ReportApiTags.REPORT_COLUMN_FILTER_DROPDOWN_VALUES,
  ],
  baseQuery: reportBaseQuery,
  endpoints: (builder) => ({
    // shared report user with internally
    sharedReportInternally: builder.mutation<
      SharedReportInternallyResponse,
      SharedReportInternallyPayload
    >({
      query: (params: SharedReportInternallyPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.SHARED_REPORT_INTERNALLY,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [ReportApiTags.SHARED_REPORT_INTERNALLY_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SharedReportInternallyResponse) => {
        return response;
      },
    }),

    // get shared report user with internally
    getSharedReportInternally: builder.query<
      ListingResponse<SharedReportInternallyResponse[]>,
      GetSharedReportInternallyPayload
    >({
      query: (params: GetSharedReportInternallyPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.SHARED_REPORT_INTERNALLY,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [ReportApiTags.SHARED_REPORT_INTERNALLY_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ListingResponse<SharedReportInternallyResponse[]>
      ) => {
        return response;
      },
    }),

    // shared report externally via email
    sharedReportViaEmail: builder.mutation<
      SharedReportViaEmailResponse,
      SharedReportViaEmailPayload
    >({
      query: (params: SharedReportViaEmailPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.SHARED_REPORT_VIA_EMAIL,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SharedReportViaEmailResponse) => {
        return response;
      },
      invalidatesTags: [ReportApiTags.SHARED_LIST],
    }),

    // shared report externally via email
    editSharedReportViaEmail: builder.mutation<
      SharedReportViaEmailResponse,
      SharedReportViaEmailPayload
    >({
      query: ({ externalShareId, ...params }: SharedReportViaEmailPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.EDIT_SHARED_REPORT_VIA_EMAIL(
            externalShareId
          ),
          method: API_METHODS.PUT,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SharedReportViaEmailResponse) => {
        return response;
      },
      invalidatesTags: [ReportApiTags.SHARED_LIST],
    }),

    // get reports under report type category
    getReportsByCatType: builder.query<
      ListingResponse<TReport[]>,
      ReportsByCategoryPayload
    >({
      query: (params: ReportsByCategoryPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.REPORTS_UNDER_CATEGORY,
          params,
          method: API_METHODS.GET,
        };
      },
      // providesTags: [ReportApiTags.CUSTOM_REPORT_CREATED], // disabled, as this doesn't work as we use same endpoint for multiple categories
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TReport[]>) => {
        return response;
      },
    }),

    // get report category fields (TODO: To be removed)
    getReportCategoryFields: builder.query<
      ListingResponse<ReportCategoryField[]>,
      ReportCategoryFieldsPayload
    >({
      query: (params: ReportCategoryFieldsPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.CATEGORY_FIELDS,
          params,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<ReportCategoryField[]>) => {
        return response;
      },
    }),
    getReportSpecificFields: builder.query<
      TReportWithFields,
      SpecificReportPayload
    >({
      query: ({ report_id, ...params }: SpecificReportPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.SPECIFIC_REPORT(report_id),
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        ReportApiTags.CUSTOM_REPORT_CREATED,
        ReportApiTags.CUSTOM_REPORT_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TReportWithFields) => {
        return response;
      },
    }),
    // Get all columns for report
    getReportAllFields: builder.query<
      ListingResponse<StandardReportFieldResponse[]>,
      ListingStandardReportFieldPayload
    >({
      query: (params: ListingStandardReportFieldPayload) => {
        const { report, ...restParams } = params;
        return {
          url: API_ENDPOINTS.REPORTS.STANDARD_REPORT_ALL_FIELDS(report),
          method: API_METHODS.GET,
          params: restParams,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ListingResponse<StandardReportFieldResponse[]>
      ) => {
        return response;
      },
    }),

    // Get event report for vehicle
    runReport: builder.query<ListingResponse<any>, StandardReportPayload>({
      query: (params: StandardReportPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.RUN_REPORT(params.id),
          method: API_METHODS.GET,
          params: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // Get event report for vehicle
    runCustomReport: builder.query<
      ListingResponse<any[]>,
      RunCustomReportPayload
    >({
      query: ({ id, ...params }: RunCustomReportPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.RUN_CUSTOM_REPORT(id),
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // create custom report
    createCustomReport: builder.mutation<
      CreateCustomReportResponse,
      CreateCustomReportPayload
    >({
      query: (params: CreateCustomReportPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.CREATE_CUSTOM_REPORT,
          method: API_METHODS.POST,
          body: params,
        };
      },
      // invalidatesTags: [ReportApiTags.CUSTOM_REPORT_CREATED], // disabled, as this doesn't work as we use same endpoint for multiple categories
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue, {
          showFieldErrorToast: true,
        });
        return baseQueryReturnValue;
      },
      transformResponse: (response: CreateCustomReportResponse) => {
        return response;
      },
    }),

    copyCustomReport: builder.mutation<
      CreateCustomReportResponse,
      CopyReportPayload
    >({
      query: ({ id, ...params }) => {
        return {
          url: API_ENDPOINTS.REPORTS.COPY_CUSTOM_REPORT(id),
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue, {
          showFieldErrorToast: true,
        });
        return baseQueryReturnValue;
      },
      transformResponse: (response: CreateCustomReportResponse) => {
        return response;
      },
    }),

    // modify custom report
    modifyCustomReport: builder.mutation<
      CreateCustomReportResponse,
      ModifyCustomReportPayload
    >({
      query: ({ id, ...params }: ModifyCustomReportPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.SPECIFIC_REPORT(id),
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        ReportApiTags.CUSTOM_REPORT_MODIFIED,
        ReportApiTags.REPORT_COLUMN_FILTER_DROPDOWN_VALUES,
      ], // disabled, as this doesn't work as we use same endpoint for multiple categories
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue, {
          showFieldErrorToast: true,
        });
        return baseQueryReturnValue;
      },
      transformResponse: (response: CreateCustomReportResponse) => {
        return response;
      },
    }),

    // delete custom report
    deleteCustomReport: builder.mutation<any, number>({
      query: (id: number) => {
        return {
          url: API_ENDPOINTS.REPORTS.SPECIFIC_REPORT(id),
          method: API_METHODS.DELETE,
        };
      },
      // invalidatesTags: [ReportApiTags.CUSTOM_REPORT_CREATED], // disabled, as this doesn't work as we use same endpoint for multiple categories
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: CreateCustomReportResponse) => {
        return response;
      },
    }),
    // start download report process
    initReportDownload: builder.query<
      RunReportDownloadProcessReponse,
      StandardReportPayload
    >({
      query: (params: StandardReportPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.RUN_REPORT(params.id),
          method: API_METHODS.GET,
          params: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: RunReportDownloadProcessReponse) => {
        return response;
      },
    }),
    getExternalShare: builder.query<
      ListingResponse<ShareReportListItem[]>,
      ShareReportListPayload
    >({
      query: (params: ShareReportListPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.EXTERNAL_SHARE_LIST,
          method: API_METHODS.GET,
          params: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: [ReportApiTags.SHARED_LIST],
    }),

    deleteExternalShareEmail: builder.mutation({
      query: (id: number) => {
        return {
          url: `/report/api/v1/external-share-email/${id}/`,
          method: API_METHODS.DELETE,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
      invalidatesTags: [ReportApiTags.SHARED_LIST],
    }),

    // report column filter dropdown values
    reportColumnFilterDropdownValues: builder.query<
      ReportColumnFilterDropdownValuesResponse,
      ReportColumnFilterDropdownValuesPayload
    >({
      query: ({
        report_id,
        ...params
      }: ReportColumnFilterDropdownValuesPayload) => {
        return {
          url: API_ENDPOINTS.REPORTS.REPORT_COLUMN_FILTER_DROPDOWN_VALUES(
            report_id
          ),
          method: API_METHODS.POST,
          body: params,
        };
      },
      keepUnusedDataFor: 60 * 5, // 5 minutes
      providesTags: [ReportApiTags.REPORT_COLUMN_FILTER_DROPDOWN_VALUES],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ReportColumnFilterDropdownValuesResponse
      ) => {
        return response;
      },
    }),
  }),
});

export const {
  // shared report user with internally
  useSharedReportInternallyMutation,
  useGetSharedReportInternallyQuery,

  // shared report externally via email
  useSharedReportViaEmailMutation,

  // get reports under report type category
  useGetReportsByCatTypeQuery,
  useLazyGetReportsByCatTypeQuery,
  useGetReportCategoryFieldsQuery,
  useGetReportSpecificFieldsQuery,
  useLazyGetReportSpecificFieldsQuery,

  useRunReportQuery,
  useRunCustomReportQuery,
  useLazyRunCustomReportQuery,
  useGetReportAllFieldsQuery,

  useCreateCustomReportMutation,
  useModifyCustomReportMutation,
  useDeleteCustomReportMutation,
  useCopyCustomReportMutation,

  // start download report process
  useLazyInitReportDownloadQuery,
  useGetExternalShareQuery,
  useEditSharedReportViaEmailMutation,
  useDeleteExternalShareEmailMutation,

  // report column filter dropdown values
  useReportColumnFilterDropdownValuesQuery,
  useLazyReportColumnFilterDropdownValuesQuery,
} = ReportAPIs;
